import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
  Sparkle, X, PhoneCall, CurrencyDollar, CaretRight,
} from '@phosphor-icons/react';
import {
  trackRecaptureUpsellFlashClicked,
  trackRecaptureUpsellFlashDismissed,
} from '../../global/analytics';
import './recaptureFlash.scss';

const DISMISS_KEY = 'recapture_flash_dismissed_at';
const FIRST_SEEN_KEY = 'recapture_flash_first_seen_at';
const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000;
const MIN_AGE_BEFORE_DISMISSIBLE_MS = 7 * 24 * 60 * 60 * 1000;

const getStorageScope = (organizationId) => {
  if (organizationId !== null && organizationId !== undefined && String(organizationId).trim()) {
    return `org:${organizationId}`;
  }

  try {
    const storedOrgId = localStorage.getItem('m8_org_id');
    if (storedOrgId) return `org:${storedOrgId}`;

    const storedOrgName = localStorage.getItem('m8_org');
    if (storedOrgName) return `org_name:${storedOrgName}`;
  } catch {
    // localStorage unavailable — keep a stable fallback scope.
  }

  return 'org:unknown';
};

const scopedKey = (key, organizationId) => `${key}:${getStorageScope(organizationId)}`;

const readTimestamp = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const ts = Number(raw);
    return Number.isFinite(ts) ? ts : null;
  } catch {
    return null;
  }
};

const writeTimestamp = (key, value) => {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // localStorage unavailable — fine to ignore.
  }
};

const isDismissed = (organizationId) => {
  const ts = readTimestamp(scopedKey(DISMISS_KEY, organizationId));
  if (ts == null) return false;
  return Date.now() - ts < DISMISS_TTL_MS;
};

const recordFirstSeen = (organizationId) => {
  const key = scopedKey(FIRST_SEEN_KEY, organizationId);
  if (readTimestamp(key) != null) return;
  writeTimestamp(key, Date.now());
};

const canDismiss = (organizationId) => {
  const firstSeen = readTimestamp(scopedKey(FIRST_SEEN_KEY, organizationId));
  if (firstSeen == null) return false;
  return Date.now() - firstSeen >= MIN_AGE_BEFORE_DISMISSIBLE_MS;
};

const RecaptureFlash = ({ visible, organizationId }) => {
  const [open, setOpen] = useState(false);
  const [dismissible, setDismissible] = useState(false);

  useEffect(() => {
    if (!visible) {
      setOpen(false);
      return;
    }

    recordFirstSeen(organizationId);
    if (isDismissed(organizationId)) {
      setOpen(false);
      return;
    }

    setOpen(true);
    setDismissible(canDismiss(organizationId));
  }, [visible, organizationId]);

  if (!open) return null;

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    writeTimestamp(scopedKey(DISMISS_KEY, organizationId), Date.now());
    trackRecaptureUpsellFlashDismissed();
    setOpen(false);
  };

  const handleClick = () => {
    trackRecaptureUpsellFlashClicked();
  };

  return (
    <Link
      to="/app/recapture"
      className="recapture-flash"
      aria-label="Learn more about Recapture"
      onClick={handleClick}
    >
      <div className="recapture-flash-icon">
        <Sparkle size={22} weight="fill" />
      </div>
      <div className="recapture-flash-body">
        <div className="recapture-flash-eyebrow"><span className="recapture-flash-badge">New</span></div>
        <div className="recapture-flash-title">
          Patient Recapture Agent,
          {' '}
          <em>automagically</em>
          {' '}
          keep your schedule full.
        </div>
        <div className="recapture-flash-subtitle">
          Books overdue patients back in. You only pay when they come back.
        </div>
        <div className="recapture-flash-tiers">
          <div className="recapture-flash-tier">
            <PhoneCall size={14} weight="fill" />
            <span>AI Voice &amp; SMS</span>
          </div>
          <div className="recapture-flash-tier recapture-flash-tier--price">
            <CurrencyDollar size={14} weight="fill" />
            <span>Starting at</span>
            <strong>$12</strong>
          </div>
        </div>
      </div>
      <div className="recapture-flash-cta">
        <span className="recapture-flash-cta-label">Activate it now</span>
        <CaretRight size={18} weight="bold" />
      </div>
      {dismissible && (
        <button
          type="button"
          className="recapture-flash-dismiss"
          aria-label="Dismiss Recapture preview"
          onClick={handleDismiss}
        >
          <X size={14} weight="bold" />
        </button>
      )}
    </Link>
  );
};

RecaptureFlash.propTypes = {
  visible: PropTypes.bool.isRequired,
  organizationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

RecaptureFlash.defaultProps = {
  organizationId: null,
};

export default RecaptureFlash;
